import React from "react";
import { useNavigate, useParams } from "react-router";
import { actionsApi } from "../../services/api";
import type {
  EmailAction,
  SmsAction,
  RunQueryAction,
  EmailActionConfig,
  SmsActionConfig,
  RunQueryActionConfig,
} from "../../types/node_action";
import {
  Mail,
  MessageSquare,
  Database,
  Save,
  X,
  ArrowLeft,
} from "lucide-react";
import { Input, Textarea } from "../ui/Input";

interface ActionEditFormProps {
  type: "email" | "sms" | "run_query";
  action?: EmailAction | SmsAction | RunQueryAction;
  isNew?: boolean;
}

export default function ActionEditForm({
  type,
  action,
  isNew = false,
}: ActionEditFormProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Form state
  const [formData, setFormData] = React.useState({
    name: action?.name || "",
    description: action?.description || "",
    is_active: action?.is_active ?? true,
    // Email specific fields
    to_address: "",
    from_email: "",
    content: "",
    subject: "",
    provider_template_code: "",
    delayed_send_minutes: 0,
    // SMS specific fields
    sender_id: "",
    // Run query specific fields
    db_name: "main",
    sql_text: "",
  });

  React.useEffect(() => {
    if (action) {
      setFormData({
        name: action.name,
        description: action.description || "",
        is_active: action.is_active,
        // Email specific fields
        to_address: (action as EmailAction).to_address || "",
        from_email: (action as EmailAction).from_email || "",
        content: (action as EmailAction).content || "",
        subject: (action as EmailAction).subject || "",
        provider_template_code:
          (action as EmailAction).provider_template_code || "",
        delayed_send_minutes: (action as EmailAction).delayed_send_minutes || 0,
        // SMS specific fields
        sender_id: (action as SmsAction).sender_id || "",
        // Run query specific fields
        db_name: (action as RunQueryAction).db_name || "main",
        sql_text: (action as RunQueryAction).sql_text || "",
      });
    }
  }, [action]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (type === "email") {
        const config: EmailActionConfig = {
          name: formData.name,
          description: formData.description,
          is_active: formData.is_active,
          to_address: formData.to_address,
          from_email: formData.from_email,
          content: formData.content,
          subject: formData.subject,
          provider_template_code: formData.provider_template_code,
          delayed_send_minutes: formData.delayed_send_minutes,
        };

        if (isNew) {
          // For new actions, we need to create a node_action first
          // This is a simplified approach - in a real app you might want a different flow
          alert(
            "Creating new standalone actions is not yet implemented. Please create actions through the nodes interface.",
          );
          return;
        } else {
          // Update existing action
          await actionsApi.setEmailConfig(Number(id), config);
        }
      } else if (type === "sms") {
        const config: SmsActionConfig = {
          name: formData.name,
          description: formData.description,
          is_active: formData.is_active,
          to_address: formData.to_address,
          sender_id: formData.sender_id,
          content: formData.content,
          provider_template_code: formData.provider_template_code,
          delayed_send_minutes: formData.delayed_send_minutes,
        };

        if (isNew) {
          alert(
            "Creating new standalone actions is not yet implemented. Please create actions through the nodes interface.",
          );
          return;
        } else {
          await actionsApi.setSmsConfig(Number(id), config);
        }
      } else if (type === "run_query") {
        const config: RunQueryActionConfig = {
          name: formData.name,
          description: formData.description,
          db_name: formData.db_name,
          sql_text: formData.sql_text,
        };

        if (isNew) {
          alert(
            "Creating new standalone actions is not yet implemented. Please create actions through the nodes interface.",
          );
          return;
        } else {
          await actionsApi.setRunQueryConfig(Number(id), config);
        }
      }

      navigate("/actions");
    } catch (e: any) {
      setError(e?.message || "Failed to save action");
    } finally {
      setSaving(false);
    }
  };

  const getActionIcon = (type: "email" | "sms" | "run_query") => {
    switch (type) {
      case "email":
        return <Mail className="h-6 w-6" />;
      case "sms":
        return <MessageSquare className="h-6 w-6" />;
      case "run_query":
        return <Database className="h-6 w-6" />;
      default:
        return null;
    }
  };

  const getActionTitle = () => {
    const actionType = type
      .replace("_", " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return isNew ? `New ${actionType} Action` : `Edit ${actionType} Action`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="text-lg text-gray-600">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate("/actions")}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Actions
            </button>
          </div>

          <div className="flex items-center gap-3">
            {getActionIcon(type)}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {getActionTitle()}
              </h1>
              <p className="mt-2 text-gray-600">
                Configure the {type.replace("_", " ")} action settings and
                parameters.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow p-6"
        >
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="text-red-800">{error}</div>
            </div>
          )}

          {/* Common Fields */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <Input
                label="Name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                variant="rounded-md"
                placeholder="Enter action name"
              />

              <Textarea
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                rows={3}
                variant="rounded-md"
                placeholder="Enter action description"
              />

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="is_active"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* Type-specific Fields */}
          {type === "email" && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Email Configuration
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="To Address"
                  type="email"
                  value={formData.to_address}
                  onChange={(e) =>
                    handleInputChange("to_address", e.target.value)
                  }
                  variant="rounded-md"
                  placeholder="recipient@example.com"
                />

                <Input
                  label="From Email"
                  type="email"
                  value={formData.from_email}
                  onChange={(e) =>
                    handleInputChange("from_email", e.target.value)
                  }
                  variant="rounded-md"
                  placeholder="sender@example.com"
                />

                <div className="col-span-2">
                  <Input
                    label="Subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) =>
                      handleInputChange("subject", e.target.value)
                    }
                    variant="rounded-md"
                    placeholder="Email subject"
                  />
                </div>

                <div className="col-span-2">
                  <Textarea
                    label="Content"
                    value={formData.content}
                    onChange={(e) =>
                      handleInputChange("content", e.target.value)
                    }
                    rows={6}
                    variant="rounded-md"
                    placeholder="Email content (supports variables like {{user_name}})"
                  />
                </div>

                <Input
                  label="Provider Template Code"
                  type="text"
                  value={formData.provider_template_code}
                  onChange={(e) =>
                    handleInputChange("provider_template_code", e.target.value)
                  }
                  variant="rounded-md"
                  placeholder="Template code from email provider"
                />

                <Input
                  label="Delayed Send (minutes)"
                  type="number"
                  min="0"
                  value={formData.delayed_send_minutes}
                  onChange={(e) =>
                    handleInputChange(
                      "delayed_send_minutes",
                      parseInt(e.target.value) || 0,
                    )
                  }
                  variant="rounded-md"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {type === "sms" && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                SMS Configuration
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="To Phone Number"
                  type="tel"
                  value={formData.to_address}
                  onChange={(e) =>
                    handleInputChange("to_address", e.target.value)
                  }
                  variant="rounded-md"
                  placeholder="+1234567890"
                />

                <Input
                  label="Sender ID"
                  type="text"
                  value={formData.sender_id}
                  onChange={(e) =>
                    handleInputChange("sender_id", e.target.value)
                  }
                  variant="rounded-md"
                  placeholder="COMPANY"
                />

                <div className="col-span-2">
                  <Textarea
                    label="Message Content"
                    value={formData.content}
                    onChange={(e) =>
                      handleInputChange("content", e.target.value)
                    }
                    rows={4}
                    variant="rounded-md"
                    placeholder="SMS message content (supports variables like {{user_name}})"
                  />
                </div>

                <Input
                  label="Provider Template Code"
                  type="text"
                  value={formData.provider_template_code}
                  onChange={(e) =>
                    handleInputChange("provider_template_code", e.target.value)
                  }
                  variant="rounded-md"
                  placeholder="Template code from SMS provider"
                />

                <Input
                  label="Delayed Send (minutes)"
                  type="number"
                  min="0"
                  value={formData.delayed_send_minutes}
                  onChange={(e) =>
                    handleInputChange(
                      "delayed_send_minutes",
                      parseInt(e.target.value) || 0,
                    )
                  }
                  variant="rounded-md"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {type === "run_query" && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Database Query Configuration
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <Input
                  label="Database Name"
                  type="text"
                  value={formData.db_name}
                  onChange={(e) => handleInputChange("db_name", e.target.value)}
                  variant="rounded-md"
                  placeholder="main"
                />

                <Textarea
                  label="SQL Query"
                  required
                  value={formData.sql_text}
                  onChange={(e) =>
                    handleInputChange("sql_text", e.target.value)
                  }
                  rows={8}
                  variant="rounded-md"
                  className="font-mono text-sm"
                  placeholder="INSERT INTO table (column) VALUES ('{{variable}}')"
                  helperText="Supports variable templating like {{user_id}}, {{session.data}}, etc. Only write operations (INSERT/UPDATE/DELETE) are allowed."
                />
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate("/actions")}
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {saving ? "Saving..." : "Save Action"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
